import React from 'react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useController, useFormContext } from 'react-hook-form';
import { FormField, FormItem } from './ui/form';
import { Label } from './ui/label';
import { Loader2 } from 'lucide-react';

export interface Option {
  label: string;
  value: string | null;
}

interface FormFieldSelectProps {
  options: Option[];
  tooltip?: string;
  placeholder?: string;
  groupLabel?: string;
  icon?: React.ReactElement;
  name: string;
  defaultValue?: string | null;
  label?: string;
  rules?: object;
  onChange?: (value: string | null | undefined) => void;
  required?: boolean;
  className?: string;
  isLoading?: boolean;
  hasControl?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  stickyItem?: Option | null;
}

const HIDDEN_NULL_VALUE = '@_NULL_@';

export const FormFieldSelect: React.FC<FormFieldSelectProps> = ({
  isLoading = false,
  options,
  placeholder,
  name,
  defaultValue,
  label,
  rules,
  onChange,
  required,
  className,
  icon,
  hasControl = true,
  disabled = false,
  clearable = false,
  stickyItem = null,
  tooltip,
}) => {
  const { control, trigger } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  const currentValue: unknown = hasControl ? field.value : defaultValue;
  const hasNullOption =
    options.some((option) => option.value === null) || stickyItem?.value === null;
  // Only map null → sentinel when a null option exists; otherwise leave unset so placeholder shows.
  const normalizedValue =
    currentValue === null
      ? hasNullOption
        ? HIDDEN_NULL_VALUE
        : undefined
      : currentValue === undefined || currentValue === ''
        ? undefined
        : String(currentValue);

  const handleSelect = (value: string | undefined) => {
    const actualValue = value === HIDDEN_NULL_VALUE ? null : value;
    if (onChange) onChange(actualValue);
    if (hasControl) field.onChange(actualValue);
  };

  const select = () => {
    return (
      <>
        <SelectTrigger
          icon={icon}
          className={`focus:border-focuscolor ${isLoading ? ' flex items-center' : ''} ${className}`}
          onBlur={() => hasControl && trigger(name)}
          disabled={isLoading || disabled}
          clearable={clearable && (hasControl ? !!field.value : !!defaultValue)}
          onClear={() => handleSelect(undefined)}
        >
          {!isLoading ? <SelectValue placeholder={placeholder} /> : <Loader2 className="h-4 w-4 animate-spin" />}
        </SelectTrigger>

        <SelectContent className="max-h-[40vh]" hideUpArrow={!!stickyItem}>
          <SelectGroup className="text-placeholder">
            {stickyItem && (
              <div key="sticky-item" className="sticky top-0 z-30 bg-background border-b border-border">
                <SelectItem value={stickyItem.value ?? HIDDEN_NULL_VALUE} className="font-semibold">
                  {stickyItem.label}
                </SelectItem>
              </div>
            )}
            {options.map((option) => (
              <SelectItem
                key={String(option.value ?? HIDDEN_NULL_VALUE)}
                value={String(option.value ?? HIDDEN_NULL_VALUE)}
                className="font-medium"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </>
    );
  };

  return (
    <FormItem className="flex flex-col gap-1.5">
      {label && (
        <Label className="p-[2px] flex items-center gap-1">
          {label}
          {required && <span className="text-error align-top">*</span>}
          {tooltip && (
            <div className="group relative flex items-center">
              <span className="cursor-pointer text-muted-foreground">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-info"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </span>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden w-max max-w-xs rounded bg-popover px-2 py-1 text-sm text-popover-foreground shadow-md group-hover:block z-50 border">
                {tooltip}
              </div>
            </div>
          )}
        </Label>
      )}

      {hasControl ? (
        <FormField
          name={name}
          control={control}
          rules={rules}
          render={() => (
            <>
              <Select
                onValueChange={async (value) => {
                  handleSelect(value);
                  await trigger(name);
                }}
                onOpenChange={(open) => !open && field.onBlur()}
                value={normalizedValue}
              >
                {select()}
              </Select>
              {fieldState.error && <p className="text-error text-xs">{fieldState.error.message}</p>}
            </>
          )}
        />
      ) : (
        <Select
          onValueChange={(value) => handleSelect(value)}
          value={normalizedValue}
          onOpenChange={(open) => !open && trigger(name)}
        >
          {select()}
        </Select>
      )}
    </FormItem>
  );
};
